import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Users,
  Scissors,
  ImageIcon,
  Settings,
  LogOut,
  Menu,
  X,
  Gift,
  DollarSign,
  Wallet,
  Star,
  ShieldCheck,
  CalendarCheck,
  AlertTriangle,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
  { label: "Calendar", icon: CalendarDays, path: "/admin/calendar" },
  { label: "Bookings", icon: Calendar, path: "/admin/bookings" },
  { label: "Clients", icon: Users, path: "/admin/clients" },
  { label: "Services", icon: Scissors, path: "/admin/services" },
  { label: "Photos", icon: ImageIcon, path: "/admin/photos" },
  { label: "Reviews", icon: Star, path: "/admin/reviews" },
  { label: "Payments", icon: Wallet, path: "/admin/settings#payment-portal" },
  { label: "Settings", icon: Settings, path: "/admin/settings" },
  { label: "Coupons & Rewards", icon: Gift, path: "/admin/rewards" },
  { label: "Revenue", icon: DollarSign, path: "/admin/revenue" },
  { label: "SMS Consent Log", icon: ShieldCheck, path: "/admin/sms-consent" },
  { label: "Calendar Sync Health", icon: CalendarCheck, path: "/admin/calendar-sync-health" },
  { label: "Bookings & Communication", icon: AlertTriangle, path: "/admin/communication-data" },
];

const BackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/admin" && !location.hash;
  if (isDashboard) return null;

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/admin");
    }
  };

  return (
    <div className="mb-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="text-muted-foreground hover:text-foreground -ml-3"
      >
        <ChevronLeft size={18} className="mr-1" />
        Back
      </Button>
    </div>
  );
};

const AdminLayout = () => {
  const { isAdmin, loading } = useAdmin();
  const { signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-background/80 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border flex flex-col transition-transform duration-300 lg:translate-x-0 overflow-y-auto",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="p-6 border-b border-sidebar-border flex items-center justify-between">
          <Link to="/admin" className="font-serif text-xl font-bold italic">
            Paradise <span className="text-sidebar-primary not-italic">Admin</span>
          </Link>
          <button className="lg:hidden text-sidebar-foreground" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const [pathOnly, hash] = item.path.split("#");
            const isActive =
              pathOnly === "/admin"
                ? location.pathname === "/admin" && !hash
                : hash
                  ? location.pathname === pathOnly && location.hash === `#${hash}`
                  : location.pathname.startsWith(pathOnly) && !location.hash;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 text-sm tracking-wide transition-colors rounded-md",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-primary font-medium"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors"
          >
            ← Back to Site
          </Link>
          <button
            onClick={signOut}
            className="flex items-center gap-3 px-4 py-3 text-sm text-destructive hover:text-destructive/80 transition-colors w-full"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        <header className="h-14 border-b border-border flex items-center px-4 lg:hidden">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu size={22} className="text-foreground" />
          </button>
          <span className="ml-3 font-serif text-lg italic">Admin</span>
        </header>
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          <BackButton />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
